import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import {
	DragSource,
	ConnectDragSource,
	DragSourceMonitor,
	DragSourceConnector
} from "react-dnd";
import { format } from "date-fns";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../api/tables";

import Typography from "@material-ui/core/Typography";
import { Reservation } from "../../api/reservations";
import { updateSingleReservation } from "../../actions/reservation-actions";
import { DropTargetData } from "./day-overview";
import { openEditReservationDialog } from "../../actions/store/app-actions";
import { Area } from '../../api/areas';

import { setActiveReservation } from "../../actions/store/overview-actions";

interface State {}

export interface ReservationItemProps extends Reservation {}

interface ConnectedReservationItemProps extends WithStyles<typeof styles> {
	activeReservationId: string;
	tables: Table[];
	areas: Area[];
	openEditReservationDialog: (reservation: Reservation) => void;
	setActiveReservation: (id: string) => void;
}

interface ReservationItemDNDProps {
	isDragging?: boolean;
	connectDragSource?: ConnectDragSource;
}

type Props = ReservationItemProps &
	ConnectedReservationItemProps &
	InjectedTranslateProps &
	ReservationItemDNDProps;

export class ReservationItem extends React.Component<Props, State> {
	public state: State = {};

	private lastClick = 0;

	handleMouseOver = () => {
		const { activeReservationId, setActiveReservation, id } = this.props;

		if (activeReservationId !== id) {
			setActiveReservation(id);
		}
	};

	handleItemClick = (
		event:
			| React.MouseEvent<HTMLDivElement>
			| React.TouchEvent<HTMLDivElement>
	) => {
		if (this.lastClick === 0) {
			this.lastClick = event.timeStamp;
		} else {
			const duration = event.timeStamp - this.lastClick;
			this.lastClick = 0;

			if (duration <= 250) {
				const {
					id,
					tableId,
					date,
					peopleCount,
					primaryName,
					time,
					phoneNumber,
					email,
					notes,
					street,
					zip,
					city,
					country
				} = this.props;

				this.props.openEditReservationDialog({
					id,
					tableId,
					date,
					peopleCount,
					primaryName,
					time,
					phoneNumber,
					email,
					notes, 
					street,
					zip,
					city,
					country
				});
			}
		}
	};

	formatNotes(notes: string) {
		if (typeof notes !== "string") {
			return "";
		}

		if (notes.length <= 30) {
			return notes;
		} else {
			return notes.substr(0, 30) + "...";
		}
	}

	private formatTime(time: Date) {
		if (time) {
			return format(time, "HH:mm");
		}

		return "";
	}

	private getAreaName(id: string) {
		const { areas } = this.props;

		const area = areas.find(a => a.id === id);

		if (area) {
			return area.name;
		}
	}

	private renderName() {
		const { primaryName, meals, classes } = this.props;

		let name = '';

		if (meals && Object.keys(meals).length > 0) {
			name += '<i class="material-icons ' + classes.icon + '">restaurant_menu</i>&nbsp;';
		}

		name += '<span>' + primaryName + '</span>';

		return name;
	}

	render() {
		const {
			classes,
			peopleCount,
			notes,
			time,
			inquiryId,
			payment,
			connectDragSource
		} = this.props;

		const statusPayed = payment && payment.state === "completed";

		return (
			connectDragSource &&
			connectDragSource(
				<div
					className={classes.root}
					onClick={this.handleItemClick}
					onTouchStart={this.handleItemClick}
					onMouseOver={this.handleMouseOver}
				>
					<div className={classNames(classes.container, classes.containerStatusManual, {
						[classes.containerStatusBookedOnline]: Boolean(inquiryId),
						[classes.containerStatusPayed]: statusPayed,
					})}>
						<div className={classes.peopleCount}>
							<Typography className={classes.peopleCountLabel}>
								{peopleCount}
							</Typography>
						</div>
						<div className={classes.content}>
							<Typography className={classes.name}>
								<div
									className={classes.nameContainer}
									dangerouslySetInnerHTML={{ __html: this.renderName() }}
								/>
							</Typography>
							{notes ? (
								<Typography className={classes.notes}>
									{this.formatNotes(notes)}
								</Typography>
							) : (
								<Typography className={classes.notes}>
									{this.formatTime(time)}
								</Typography>
							)}
						</div>
					</div>
				</div>
			)
		);
	}
}

@DragSource(
	"ReservationItem",
	{
		beginDrag(props: Props) {
			return { id: props.id };
		},
		endDrag(props: Props, monitor: DragSourceMonitor) {
			const dropResult = monitor.getDropResult() as DropTargetData;

			if (dropResult) {
				updateSingleReservation(props.id, {
					tableId: dropResult.tableId
				});
			}
		}
	},
	(connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
		connectDragSource: connect.dragSource(),
		isDragging: monitor.isDragging()
	})
)
class DecoratedReservationItem extends ReservationItem {}

const mapStateToProps = (state: StoreState) => ({
	activeReservationId: state.overviewActiveReservationId,
	areas: state.areas,
	tables: state.tables
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setActiveReservation: (id: string) => dispatch(setActiveReservation(id)),
	openEditReservationDialog: (reservation: Reservation) =>
		dispatch(openEditReservationDialog(reservation))
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			cursor: "grab",
		},
		container: {
			padding: "5px 10px",
			border: "1px solid lightgray",
			background: "white",
			display: "flex",
			alignItems: "center",
			borderRadius: 4,
			minHeight: 60
		},
		containerStatusManual: {
			borderLeft: "6px solid " + theme.status.reservation.manual
		},
		containerStatusBookedOnline: {
			borderLeft: "6px solid " + theme.status.reservation.bookedOnline
		},
		containerStatusPayed: {
			borderLeft: "6px solid " + theme.status.reservation.payed
		},
		peopleCount: {
			flexGrow: 0,
			fontSize: 0,
			paddingRight: 10,
			paddingLeft: 8,
			borderRight: "1px solid lightgray",
			marginRight: 10
		},
		peopleCountLabel: {
			fontSize: 28,
			minWidth: 24,
			textAlign: "center"
		},
		nameContainer: {
			display: "flex",
			alignItems: "center",
		},
		icon: {
			fontSize: "initial",
			color: theme.palette.primary.main,
			marginTop: -2
		},
		content: {
			flexGrow: 1,
			paddingRight: 10
		},
		buttonBar: {
			flexGrow: 0
		},
		button: {},
		notes: {
			fontStyle: "italic"
		},
		name: {
			fontWeight: 500
		}
	});

export const ConnectedReservationItem = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(DecoratedReservationItem)
	)
);
